import type { BookingPage, TransportOptions } from '../../api/transport'
import type { Booking, CarriedTrip } from './booking'

/**
 * "Book the same trip again" (M40-T11, owner Q11). The earlier trip's
 * answers go into a new booking that starts at the safety check, which is
 * always asked again.
 *
 * In memory only (M39-Q3): from the booking page to `/transport` they pass
 * through this module, never a URL, `history.state` or any storage, so a
 * reload loses them. The flow reads them as it starts and clears them.
 */

let pending: CarriedTrip | null = null

export function carryToNewBooking(trip: CarriedTrip): void {
  pending = trip
}

/** The carried trip, if any; read while the flow starts. */
export function readCarried(): CarriedTrip | null {
  return pending
}

/** Once the flow has taken it, so a later visit starts empty. */
export function clearCarried(): void {
  pending = null
}

/** From Screen 6, where the whole trip is still in memory. */
export function carriedFromBooking(booking: Booking): CarriedTrip | null {
  const { service, trip } = booking
  if (!service || !trip) return null
  return {
    service,
    pickupAreaId: trip.pickupAreaId,
    pickupAreaName: null,
    pickupAddress: trip.pickupAddress,
    pickupLandmark: trip.pickupLandmark,
    hospitalId: trip.hospitalId,
    hospitalName: null,
    journey: trip.journey,
    returnMode: trip.returnMode,
    hireLength: trip.hireLength,
  }
}

/** From the booking page (T08's answer): names, and no address, which the
 * page never has (§E.8). */
export function carriedFromPage(page: BookingPage): CarriedTrip {
  const carHire = page.service === 'car_hire'
  return {
    service: page.service,
    pickupAreaId: null,
    pickupAreaName: page.pickup_area,
    pickupAddress: null,
    pickupLandmark: null,
    hospitalId: null,
    hospitalName: page.hospital,
    journey: carHire ? null : page.return_mode ? 'return' : 'one_way',
    returnMode: page.return_mode,
    hireLength: null,
  }
}

/** Names matched to today's options; an area or hospital no longer offered
 * is left for the customer to choose. */
export function resolveCarried(
  trip: CarriedTrip,
  options: TransportOptions,
): CarriedTrip {
  const area =
    options.areas.find((a) => a.id === trip.pickupAreaId) ??
    options.areas.find((a) => a.name === trip.pickupAreaName)
  const hospital =
    options.hospitals.find((h) => h.id === trip.hospitalId) ??
    options.hospitals.find((h) => h.name === trip.hospitalName)
  return {
    ...trip,
    pickupAreaId: area?.id ?? null,
    hospitalId: hospital?.id ?? null,
  }
}
